import React, { useState } from "react";
import { Star } from "lucide-react";
import BotanicalBackdrop from "@/components/BotanicalBackdrop";
import { REVIEWS } from "@/data/reviews";
import { useProducts } from "@/context/ProductsContext";
import usePageTitle from "@/hooks/usePageTitle";

const Stars = ({ n }) => (
  <div className="flex items-center gap-0.5 text-[var(--hk-orange)]" aria-label={`${n} out of 5`}>
    {Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={13}
        strokeWidth={1.4}
        fill={i < n ? "currentColor" : "none"}
      />
    ))}
  </div>
);

const ReviewsPage = () => {
  usePageTitle("Customer Reviews");
  const { allProducts } = useProducts();
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? REVIEWS : REVIEWS.filter((r) => r.productSlug === filter);

  const products = allProducts.map((p) => ({ slug: p.slug, label: (p.name || "").replace(" for ", " — ").replace(" Pure Pulp", "") }));

  return (
    <>
      <section className="relative pt-16 pb-8 lg:pt-24 lg:pb-12 overflow-hidden">
        <BotanicalBackdrop variant="sideRight" />
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <div className="label-eyebrow">Reviews</div>
          <h1 className="serif mt-4 text-[42px] sm:text-[54px] lg:text-[60px] leading-[1.05] text-[var(--hk-ink)]" data-testid="reviews-page-title">
            Rituals, in the words of those who <span className="italic-accent text-[var(--hk-orange-deep)]">choose HerbKranti.</span>
          </h1>
        </div>
      </section>

      <section className="relative py-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {/* Filters */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10" data-testid="reviews-filters">
            <button
              onClick={() => setFilter("all")}
              data-testid="reviews-filter-all"
              className={
                "px-4 py-2 rounded-full text-[12px] tracking-widest uppercase border transition-colors " +
                (filter === "all"
                  ? "bg-[var(--hk-ink)] text-white border-[var(--hk-ink)]"
                  : "bg-white text-[var(--hk-ink-2)] border-[var(--hk-border)] hover:border-[var(--hk-orange)]")
              }
            >
              All <span className="opacity-60 ml-1">({REVIEWS.length})</span>
            </button>
            {products.map((p) => {
              const count = REVIEWS.filter((r) => r.productSlug === p.slug).length;
              return (
                <button
                  key={p.slug}
                  onClick={() => setFilter(p.slug)}
                  data-testid={`reviews-filter-${p.slug}`}
                  className={
                    "px-4 py-2 rounded-full text-[12px] tracking-widest uppercase border transition-colors " +
                    (filter === p.slug
                      ? "bg-[var(--hk-ink)] text-white border-[var(--hk-ink)]"
                      : "bg-white text-[var(--hk-ink-2)] border-[var(--hk-border)] hover:border-[var(--hk-orange)]")
                  }
                >
                  {p.label} <span className="opacity-60 ml-1">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
            {filtered.map((r) => {
              const prod = allProducts.find((p) => p.slug === r.productSlug);
              return (
                <figure key={r.id} className="hk-card p-8 flex flex-col" data-testid={`review-${r.id}`}>
                  <Stars n={r.rating} />
                  <blockquote className="serif mt-5 text-[18px] leading-[1.55] text-[var(--hk-ink)] flex-1">"{r.text}"</blockquote>
                  <figcaption className="mt-6 pt-6 border-t border-[var(--hk-border)] flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] flex items-center justify-center serif text-[var(--hk-orange-deep)]">
                      {r.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium text-[var(--hk-ink)]">{r.name}</div>
                      <div className="label-eyebrow !text-[10px] mt-1 truncate">{r.city} · {prod ? prod.name : ""}</div>
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="hk-card p-16 text-center" data-testid="reviews-empty">
              <div className="serif text-[22px] text-[var(--hk-ink)]">Customer experiences will be added after verified purchases.</div>
              <p className="mt-2 text-[13px] text-[var(--hk-ink-2)]">We publish only real, verified customer reviews. Reviews for this product will appear here after launch.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ReviewsPage;
